import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TaskType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import type { TaskCardOpenIntent } from '../../../TaskCard';
import { useTask } from '../../../TaskContext';
import type { KanbanRelatedTasksTarget } from '../KanbanCard';
import {
    applyOrder,
    loadOrder,
    saveOrder,
    type KanbanColumnOrder,
} from '../helpers/kanbanColumnOrder';
import { KANBAN_COLUMNS } from './constants';
import { sortBoardTasks } from './boardHelpers';

export interface KanbanColumnView {
    status: number;
    tone: string;
    tasks: TaskType[];
}

const useKanbanBoard = () => {
    const { auth } = useAuth();
    const { tasks, showSpinner, tasksStatus, triggerListRefresh } = useTask();
    const userId = Number(auth?.user?.ID || 0);

    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    const [openIntent, setOpenIntent] = useState<TaskCardOpenIntent>(null);
    const [relatedTasks, setRelatedTasks] = useState<KanbanRelatedTasksTarget | null>(null);
    const [columnOrder, setColumnOrder] = useState<KanbanColumnOrder>({});

    useEffect(() => {
        setColumnOrder(loadOrder(userId));
    }, [userId]);

    const persistOrder = useCallback(
        (next: KanbanColumnOrder) => {
            setColumnOrder(next);
            saveOrder(userId, next);
        },
        [userId],
    );

    const columns = useMemo((): KanbanColumnView[] => {
        const filterSet =
            tasksStatus && tasksStatus.length > 0 ? new Set(tasksStatus.map(Number)) : null;

        return KANBAN_COLUMNS.filter((col) => (filterSet ? filterSet.has(col.status) : true)).map(
            (col) => {
                const filtered = ((tasks || []) as TaskType[]).filter(
                    (task) =>
                        Number(task.taskStatus) === col.status && !task.taskKanbanHide,
                );
                return {
                    ...col,
                    tasks: applyOrder(filtered, col.status, columnOrder, sortBoardTasks),
                };
            },
        );
    }, [tasks, tasksStatus, columnOrder]);

    const taskById = useMemo(() => {
        const map = new Map<number, TaskType>();
        for (const col of columns) {
            for (const task of col.tasks) map.set(task.ID, task);
        }
        return map;
    }, [columns]);

    const openTask = useCallback((taskId: number) => {
        setOpenIntent(null);
        setOpenTaskId(taskId);
    }, []);

    const openTaskWithIntent = useCallback((taskId: number, intent: TaskCardOpenIntent) => {
        setOpenIntent(intent);
        setOpenTaskId(taskId);
    }, []);

    const hideTaskCard = useCallback(() => {
        setOpenTaskId(null);
        setOpenIntent(null);
    }, []);

    const clearOpenIntent = useCallback(() => {
        setOpenIntent(null);
    }, []);

    return {
        userId,
        tasks,
        showSpinner,
        columns,
        taskById,
        columnOrder,
        persistOrder,
        triggerListRefresh,
        openTaskId,
        openIntent,
        relatedTasks,
        setRelatedTasks,
        openTask,
        openTaskWithIntent,
        hideTaskCard,
        clearOpenIntent,
    };
};

export default useKanbanBoard;
